import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthGuard, AuthGuardFullPage } from "./components/AuthGuard.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { ToastProvider } from "./context/ToastContext.js";
import { ThemeProvider } from "./context/ThemeContext.js";
import { ConfirmDialogProvider } from "./components/ui/confirm-dialog.js";
import LoginPage from "./pages/LoginPage.js";
import SetupMfaPage from "./pages/SetupMfaPage.js";
import OnboardingWizardPage from "./pages/OnboardingWizardPage.js";
import DashboardPage from "./pages/DashboardPage.js";
import EmployeesPage from "./pages/EmployeesPage.js";
import EmployeeProfilePage from "./pages/EmployeeProfilePage.js";
import AttendancePage from "./pages/AttendancePage.js";
import LeavePage from "./pages/LeavePage.js";
import TimesheetPage from "./pages/TimesheetPage.js";
import PayrollPage from "./pages/PayrollPage.js";
import AssetsPage from "./pages/AssetsPage.js";
import OffboardingPage from "./pages/OffboardingPage.js";
import ExpensesPage from "./pages/ExpensesPage.js";
import PerformancePage from "./pages/PerformancePage.js";
import GrievancePage from "./pages/GrievancePage.js";
import ContractorsPage from "./pages/ContractorsPage.js";
import ReportsPage from "./pages/ReportsPage.js";
import EmployeeReportPage from "./pages/reports/EmployeeReportPage.js";
import DocumentReportPage from "./pages/reports/DocumentReportPage.js";
import LeaveReportPage from "./pages/reports/LeaveReportPage.js";
import TimesheetReportPage from "./pages/reports/TimesheetReportPage.js";
import HrDashboardPage from "./pages/reports/HrDashboardPage.js";
import HeadcountReportPage from "./pages/reports/HeadcountReportPage.js";
import AttritionReportPage from "./pages/reports/AttritionReportPage.js";
import ProbationReportPage from "./pages/reports/ProbationReportPage.js";
import LopReportPage from "./pages/reports/LopReportPage.js";
import LearningPage from "./pages/LearningPage.js";
import SuccessionPage from "./pages/SuccessionPage.js";
import AdminPage from "./pages/AdminPage.js";
import LeaveAdminPage from "./pages/LeaveAdminPage.js";
import EmailTemplatesAdminPage from "./pages/EmailTemplatesAdminPage.js";
import VirtualHrAdminPage from "./pages/VirtualHrAdminPage.js";
import IssueTrackerSettingsPage from "./pages/admin/IssueTrackerSettingsPage.js";
import MigrationSimulatorPage from "./pages/admin/MigrationSimulatorPage.js";
import PendingChangesPage from "./pages/PendingChangesPage.js";
import FieldPermissionsPage from "./pages/FieldPermissionsPage.js";
import AuditPage from "./pages/AuditPage.js";
import ShiftHistoryPage from "./pages/ShiftHistoryPage.js";
import AttendanceSourcesPage from "./pages/AttendanceSourcesPage.js";
import IssueTrackerPage from "./pages/IssueTrackerPage.js";
import OrgChartPage from "./pages/OrgChartPage.js";
import OnboardingStartPage from "./pages/OnboardingStartPage.js";
import HandbookSignPage from "./pages/HandbookSignPage.js";

export default function App() {
  return (
    <ThemeProvider>
    <BrowserRouter>
      <ErrorBoundary>
      <ToastProvider>
      <ConfirmDialogProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/onboarding/start" element={<OnboardingStartPage />} />
        <Route path="/handbook/sign" element={<HandbookSignPage />} />
        <Route element={<AuthGuardFullPage />}>
          <Route path="/setup-mfa" element={<SetupMfaPage />} />
          <Route path="/onboarding/wizard" element={<OnboardingWizardPage />} />
        </Route>
        <Route element={<AuthGuard />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/employees/new" element={<EmployeeProfilePage />} />
          <Route path="/employees/:id/edit" element={<EmployeeProfilePage />} />
          <Route path="/employees/:id/view" element={<EmployeeProfilePage />} />
          <Route path="/employees/*" element={<EmployeesPage />} />
          <Route path="/onboarding/*" element={<Navigate to="/employees" replace />} />
          <Route path="/attendance/*" element={<AttendancePage />} />
          <Route path="/leave/*" element={<LeavePage />} />
          <Route path="/timesheet/*" element={<TimesheetPage />} />
          <Route path="/payroll/*" element={<PayrollPage />} />
          <Route path="/assets/*" element={<AssetsPage />} />
          <Route path="/offboarding/*" element={<OffboardingPage />} />
          <Route path="/expenses/*" element={<ExpensesPage />} />
          <Route path="/contractors/*" element={<ContractorsPage />} />
          <Route path="/performance/*" element={<PerformancePage />} />
          <Route path="/grievance/*" element={<GrievancePage />} />
          <Route path="/reports/employees" element={<EmployeeReportPage />} />
          <Route path="/reports/documents" element={<DocumentReportPage />} />
          <Route path="/reports/leave" element={<LeaveReportPage />} />
          <Route path="/reports/timesheet" element={<TimesheetReportPage />} />
          <Route path="/reports/dashboard" element={<HrDashboardPage />} />
          <Route path="/reports/headcount" element={<HeadcountReportPage />} />
          <Route path="/reports/attrition" element={<AttritionReportPage />} />
          <Route path="/reports/probation" element={<ProbationReportPage />} />
          <Route path="/reports/lop" element={<LopReportPage />} />
          {/* Payroll Input moved to the first tab of the Payroll page. */}
          <Route path="/reports/payroll" element={<Navigate to="/payroll" replace />} />
          <Route path="/reports/*" element={<ReportsPage />} />
          <Route path="/learning/*" element={<LearningPage />} />
          <Route path="/succession/*" element={<SuccessionPage />} />
          <Route path="/admin/pending-changes"      element={<PendingChangesPage />} />
          <Route path="/admin/field-permissions"  element={<FieldPermissionsPage />} />
          <Route path="/admin/audit"              element={<AuditPage />} />
          <Route path="/admin/shift-history"      element={<ShiftHistoryPage />} />
          <Route path="/admin/attendance-sources" element={<AttendanceSourcesPage />} />
          <Route path="/admin/leave/*"            element={<LeaveAdminPage />} />
          <Route path="/admin/email-templates/*"  element={<EmailTemplatesAdminPage />} />
          <Route path="/admin/virtual-hr"         element={<VirtualHrAdminPage />} />
          <Route path="/admin/issue-tracker"      element={<IssueTrackerSettingsPage />} />
          <Route path="/admin/migration-v2"       element={<MigrationSimulatorPage />} />
          <Route path="/admin/*" element={<AdminPage />} />
          <Route path="/issues/*" element={<IssueTrackerPage />} />
          <Route path="/org-chart" element={<OrgChartPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </ConfirmDialogProvider>
      </ToastProvider>
      </ErrorBoundary>
    </BrowserRouter>
    </ThemeProvider>
  );
}
